import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";

export function GoogleButton({ testid }) {
  const { t } = useI18n();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} data-testid={testid}
            className="w-full rounded-full gap-2">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      {t("auth.google", "Continue with Google")}
    </Button>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <BrandMark className="h-10 w-10" animate />
          <span className="font-serif text-2xl font-semibold">EverMind</span>
        </Link>
        <div className="ambient-shadow rounded-3xl border border-border/60 bg-card p-8">
          <h1 className="font-serif text-2xl font-semibold">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.onboarding_completed ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.welcomeBack")} subtitle={t("auth.welcomeSub")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1.5 rounded-xl" data-testid="login-email-input" placeholder="you@example.com" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                 className="mt-1.5 rounded-xl" data-testid="login-password-input" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit-button">
          {loading ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
      </form>
      <Link to="/forgot-password" className="mt-3 block text-center text-sm text-primary hover:underline" data-testid="forgot-password-link">
        {t("forgot.link", "Forgot your password?")}
      </Link>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("auth.or")} <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton testid="login-google-button" />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.newHere")} <Link to="/register" className="text-primary hover:underline" data-testid="go-register-link">{t("auth.createAccount")}</Link>
      </p>
    </AuthShell>
  );
}

export { AuthShell };
